import { Controller } from '@hotwired/stimulus';
import {
  debounce,
  eventListener,
  id,
  observer,
} from '@inked/utilities';
import Mustache from 'mustache';
import Swiper from 'swiper';
import {
  Pagination,
  Thumbs,
  Zoom,
} from 'swiper/modules';

export class SlideshowController extends Controller {
  static targets = [
    'indicator',
    'indicators',
    'indicatorTemplate',
    'slide',
    'thumbnail',
    'thumbnails',
    'track',
  ];
  static values = {
    current: {
      default: 0,
      type: Number,
    },
    loop: {
      default: false,
      type: Boolean,
    },
    maxZoomRatio: {
      default: 3,
      type: Number,
    },
    minZoomRatio: {
      default: 1,
      type: Number,
    },
    spaceBetween: Number,
    triggerAttribute: {
      default: 'data-slideshow-index',
      type: String,
    },
    triggerSelector: String,
    zoom: {
      default: false,
      type: Boolean,
    },
  };

  connect() {
    this.slideshow = new Swiper(this.element, {
      focusableElements: 'button',
      loop: this.loopValue,
      modules: [
        ... this.hasIndicatorsTarget ? [Pagination] : [],
        ... this.hasThumbnailsTarget ? [Thumbs] : [],
        ... this.zoomValue ? [Zoom] : [],
      ],
      on: {
        activeIndexChange: swiper => this.currentValue = swiper.activeIndex,
        destroy: () => {
          if (this.triggerSelectorValue) {
            this.stopTriggerSelectorClickEvents();
            this.stopTriggerSelectorObserver();
          }
          if (this.zoomValue) this.stopSlideWheelEvents();
        },
        init: swiper => {
          if (this.triggerSelectorValue) {
            this.stopTriggerSelectorClickEvents = eventListener(
              this.triggerSelectorValue,
              'click',
              event => this.onTriggerSelectorClick(event),
            );

            this.stopTriggerSelectorObserver = observer(
              document.body,
              'mutation',
              mutations => {
                for (const mutation of mutations) {
                  for (const node of mutation.addedNodes) {
                    if (node.nodeType !== Node.ELEMENT_NODE) continue;

                    if (node.matches(this.triggerSelectorValue)) {
                      eventListener(node, 'click', event => this.onTriggerSelectorClick(event));

                      continue;
                    }

                    [...node.querySelectorAll(this.triggerSelectorValue)].forEach(childNode => {
                      eventListener(childNode, 'click', event => this.onTriggerSelectorClick(event));
                    });
                  }
                }
              },
              {
                childList: true,
                subtree: true,
              },
            );
          }

          if (this.zoomValue) {
            this.stopSlideWheelEvents = eventListener(
              this.slideTargets,
              'wheel',
              debounce(event => {
                event.preventDefault();

                const ratio = event.deltaY < 0
                  ? swiper.zoom.scale + 0.25
                  : swiper.zoom.scale - 0.25;

                swiper.zoom.in(Math.max(this.minZoomRatioValue, Math.min(this.maxZoomRatioValue, ratio)));
              }, 10),
            );
          }
        },
        zoomChange: (_swiper, scale, _target, container) => {
          if (scale > 1) return container.style.cursor = 'move';

          container.style.cursor = null;
        },
      },
      pagination: this.hasIndicatorsTarget
        ? {
          el: this.indicatorsTarget,
          renderBullet: index => Mustache.render(this.indicatorTemplateTarget.innerHTML, {
            current: this.currentValue == index,
          }),
        }
        : undefined,
      slideClass: (() => {
        const className = `slideshow__slide-${id()}`;

        this.slideTargets.forEach(slide => slide.classList.add(className));

        return className;
      })(),
      spaceBetween: this.spaceBetweenValue,
      thumbs: this.hasThumbnailsTarget
        ? {
          swiper: new Swiper(this.thumbnailsTarget, {
            loop: true,
            slideClass: (() => {
              const className = `slideshow__thumbnail-${id()}`;

              this.thumbnailTargets.forEach(thumbnail => thumbnail.classList.add(className));

              return className;
            })(),
            slidesPerView: 4,
            spaceBetween: this.spaceBetweenValue,
            wrapperClass: (() => {
              const className = `slideshow__thumbnails-${id()}`;

              this.thumbnailsTarget.children[0].classList.add(className);

              return className;
            })(),
          }),
        }
        : undefined,
      wrapperClass: (() => {
        const className = `slideshow__track-${id()}`;

        this.trackTarget.classList.add(className);

        return className;
      })(),
      zoom: this.zoomValue
        ? {
          containerClass: (() => {
            const className = `slideshow__slide-zoom-${id()}`;

            this.slideTargets.forEach(slide => {
              const container = slide.children[0];

              container.classList.add(className);

              if ([ 'img', 'picture', 'canvas' ].includes(container.children[0].tagName.toLowerCase())) return;

              container.children[0].classList.add('swiper-zoom-target');
            });

            return className;
          })(),
          maxRatio: this.maxZoomRatioValue,
          minRatio: this.minZoomRatioValue,
        }
        : undefined,
    });
  }

  currentValueChanged(value) {
    this.slideTargets.forEach((target, targetIndex) => target.ariaHidden = targetIndex !== value);

    if (this.hasIndicatorTarget) {
      this.indicatorTargets.forEach((target, targetIndex) => target.ariaCurrent = targetIndex === value);
    }
  }

  disconnect() {
    this.slideshow.destroy();
  }

  go(event, speed = null) {
    const index = event.params?.index || event.currentTarget.getAttribute(this.triggerAttributeValue) || 0;

    this.loopValue
      ? this.slideshow.slideToLoop(index, speed)
      : this.slideshow.slideTo(index, speed);
  }

  next() {
    this.slideshow.slideNext();
  }

  onTriggerSelectorClick(event) {
    this.go(event, 0);
  }

  previous() {
    this.slideshow.slidePrev();
  }
}
